/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useStore } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import { loadingIndicatorActions } from '@opengeoweb/store';
import { openLayersGetMapImageStore } from '@opengeoweb/webmap-react';

const LOADING_INDICATOR_UPDATE_INTERVAL_MS = 200;

// This hook checks periodically if the loading completeness (percentage) of a set of layerIds has changed. This avoids an action storm of setGetMapIsLoading actions. Should only be used in mapview.
export const useMapViewLoadingIndicator = (
  mapId: string,
  layerIds: string[],
): void => {
  const { dispatch } = useStore<CoreAppStore>();

  const setGetMapLoadingPercentage = React.useCallback(
    (percentageComplete: number): void => {
      dispatch(
        loadingIndicatorActions.setGetMapIsLoading({
          id: mapId,
          getMapLoadingPercentage: percentageComplete,
        }),
      );
    },
    [dispatch, mapId],
  );

  // Check periodically if percentage is changed. If this is the case, dispatch an action.
  const percentageCompleteRef = React.useRef(100);
  const numberDoneRef = React.useRef(0);
  React.useEffect(() => {
    const intervalId = setInterval(() => {
      const { percentage: percentageComplete, doneLoading } =
        openLayersGetMapImageStore.loadDuration.getTotalLoadingPercentageForIds(
          layerIds,
        );
      if (
        percentageComplete !== percentageCompleteRef.current ||
        doneLoading !== numberDoneRef.current
      ) {
        numberDoneRef.current = doneLoading;
        percentageCompleteRef.current = percentageComplete;
        setGetMapLoadingPercentage(percentageComplete);
      }
    }, LOADING_INDICATOR_UPDATE_INTERVAL_MS);
    return (): void => {
      clearInterval(intervalId);
    };
  }, [layerIds, mapId, setGetMapLoadingPercentage]);
};
